import React from 'react';
import { Link } from 'react-router-dom';
import useDocumentMeta from '../useDocumentMeta';
import TestYourselfPrompt from '../TestYourselfPrompt';

const TOOLS = [
    {
        to: '/study-guide/devolved-nations',
        emoji: '🏴',
        title: 'Devolved Parliaments & Legal Systems',
        desc: 'What Westminster controls versus what Edinburgh, Cardiff and Belfast control: Scotland, Wales and Northern Ireland devolution at a glance.',
        available: true,
    },
    {
        to: '/study-guide/court-hierarchy',
        emoji: '⚖️',
        title: 'Court Hierarchy',
        desc: "Magistrates' Courts, Crown Courts, and the Supreme Court: how the justice system is structured and how cases move through it.",
        available: true,
    },
    {
        to: '/study-guide/elections-cheat-sheet',
        emoji: '🗳️',
        title: 'Elections & Voting Cheat Sheet',
        desc: 'First-past-the-post explained, voting age, standing for election, and the House of Commons vs House of Lords: the tricky numbers in one place.',
        available: true,
    },
];

function ToolCard({ tool }) {
    const content = (
        <>
            <div className="text-3xl mb-3">{tool.emoji}</div>
            <h3 className="font-bold text-slate-900 dark:text-slate-50 mb-1">{tool.title}</h3>
            <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{tool.desc}</p>
            {!tool.available && (
                <span className="inline-block mt-3 text-[11px] font-bold uppercase tracking-wider text-indigo-500 dark:text-indigo-400">Coming soon</span>
            )}
        </>
    );

    const baseClass = "block rounded-2xl border p-6 shadow-sm transition bg-white dark:bg-slate-900 border-slate-100 dark:border-slate-800";

    if (!tool.available) {
        return <div className={`${baseClass} opacity-60 cursor-not-allowed`}>{content}</div>;
    }

    return (
        <Link to={tool.to} className={`${baseClass} hover:border-indigo-200 dark:hover:border-indigo-800 hover:shadow-md`}>
            {content}
        </Link>
    );
}

const SITE_URL = "https://lifeinukcoach.co.uk";

const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: "Study Guide", item: `${SITE_URL}/study-guide` },
        { "@type": "ListItem", position: 3, name: "Government and Law", item: `${SITE_URL}/study-guide/government-and-law` },
    ],
};

export default function GovernmentAndLaw() {
    useDocumentMeta({
        title: "Government and Law Study Guide: Life in the UK Test 2026",
        description: "Master the Government, Parliament, and Law chapter of the official Life in the UK Test handbook, covering UK democracy and the justice system.",
        path: "/study-guide/government-and-law",
        jsonLd: breadcrumbSchema,
    });

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto bg-white dark:bg-slate-900 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 p-8 md:p-12">
                <Link to="/study-guide" className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-semibold text-sm mb-6 inline-block">
                    ← Back to Study Guide
                </Link>
                <h1 className="text-3xl font-extrabold text-slate-900 dark:text-slate-50 mb-4">
                    Government and Law Study Guide: Life in the UK Test 2026
                </h1>
                <p className="text-slate-600 dark:text-slate-300 mb-6 leading-relaxed">
                    Master the complete Government, Parliament, and Law chapter of the official Life in the UK Test handbook.
                    This guide covers the structure of UK democracy, the justice system, and your role as a British citizen.
                </p>

                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-8 mb-3">Key Government Topics Covered</h2>
                <ul className="list-disc pl-5 text-slate-700 dark:text-slate-300 space-y-2 mb-6">
                    <li><strong>The British Constitution:</strong> Understand that the UK constitution is unwritten and based on common law and conventions.</li>
                    <li><strong>The Monarchy:</strong> Learn about the role of the King as Head of State, and how the UK operates as a constitutional monarchy.</li>
                    <li><strong>Parliament:</strong> Study the roles of the House of Commons, the House of Lords, and how laws are debated and passed.</li>
                    <li><strong>The Government:</strong> Discover how the Prime Minister, Cabinet, and Opposition parties lead the nation.</li>
                    <li><strong>The Justice System:</strong> Memorize the differences between civil and criminal courts, and police/magistrate structures.</li>
                </ul>

                <h2 className="text-xl font-bold text-slate-800 dark:text-slate-100 mt-8 mb-3">Visual Study Tools</h2>
                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                    {TOOLS.map(tool => <ToolCard key={tool.title} tool={tool} />)}
                </div>

                <div className="bg-indigo-50 dark:bg-indigo-950 border border-indigo-100 dark:border-indigo-900 rounded-xl p-6 mb-8">
                    <h3 className="font-bold text-indigo-950 dark:text-indigo-200 mb-2">Did you know?</h3>
                    <p className="text-sm text-indigo-900 dark:text-indigo-300">
                        Government & Law questions make up a huge part of the real exam. Make sure you know details like the minimum voting age (18), the maximum time between general elections (5 years), and the unwritten nature of the British Constitution.
                    </p>
                </div>
                <TestYourselfPrompt topic="government_and_law" />
</div>
        </div>
    );
}
